import { useCallback, useRef, useState } from "react";
import { Loader2, UploadCloud, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AttachmentAPI, extractError } from "@/lib/api";
import { toast } from "sonner";

const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime,application/pdf";
const ALLOWED_MIME_PREFIXES = ["image/", "video/", "application/pdf"];
const MAX_MB = 25;
const KIND_OPTIONS = [
    { v: "before", l: "Before" },
    { v: "after", l: "After" },
    { v: "damage", l: "Damage" },
    { v: "inspection", l: "Inspection" },
    { v: "parts_invoice", l: "Parts invoice" },
    { v: "other", l: "Other" },
];

export function AttachmentDropzone({ jobId, onUploaded, disabled }) {
    const [files, setFiles] = useState([]); // [{file, kind, caption}]
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const inputRef = useRef(null);

    const addFiles = useCallback((list) => {
        const next = [];
        let err = null;
        for (const f of list) {
            if (f.size > MAX_MB * 1024 * 1024) {
                err = `FILE_TOO_LARGE: ${f.name} is ${(f.size / 1024 / 1024).toFixed(1)} MB — max ${MAX_MB} MB.`;
                toast.error(err);
                continue;
            }
            // Client-side MIME sanity (server still re-sniffs and is authoritative).
            const declared = f.type || "";
            const isAllowed = ALLOWED_MIME_PREFIXES.some((p) => declared.startsWith(p));
            if (declared && !isAllowed) {
                err = `UNSUPPORTED_MEDIA_TYPE: ${f.name} (${declared}) is not an allowed file type.`;
                toast.error(err);
                continue;
            }
            next.push({ file: f, kind: "inspection", caption: "" });
        }
        if (err) setUploadError(err);
        else if (next.length) setUploadError(null);
        setFiles((p) => [...p, ...next]);
    }, []);

    function onDrop(e) {
        e.preventDefault();
        if (disabled) return;
        if (e.dataTransfer.files && e.dataTransfer.files.length) {
            addFiles(Array.from(e.dataTransfer.files));
        }
    }

    function update(idx, key, value) {
        setFiles((p) => p.map((f, i) => (i === idx ? { ...f, [key]: value } : f)));
    }
    function remove(idx) { setFiles((p) => p.filter((_, i) => i !== idx)); }

    async function upload() {
        if (!files.length) return;
        setUploading(true);
        setUploadError(null);
        try {
            const meta = files.map((f) => ({ kind: f.kind, caption: f.caption }));
            const res = await AttachmentAPI.upload(jobId, files.map((f) => f.file), meta);
            // The server returns per-file errors inside `items` for partial failures
            // — surface the first one to the user as upload-error so the suite can
            // assert UNSUPPORTED_MEDIA_TYPE / FILE_TOO_LARGE on disguised payloads.
            const failed = (res.items || []).find((it) => it && it.error);
            if (failed) {
                setUploadError(failed.error_code ? `${failed.error_code}: ${failed.error}` : failed.error);
            }
            toast.success(`Uploaded ${res.uploaded} file(s).`);
            setFiles([]);
            onUploaded && onUploaded(res.items || []);
        } catch (e) {
            const msg = extractError(e);
            setUploadError(msg);
            toast.error(msg);
        } finally { setUploading(false); }
    }

    return (
        <div className="space-y-3" data-testid="attachment-dropzone">
            <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                className={`flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-6 text-center text-sm transition-colors ${disabled ? "opacity-60" : "hover:border-brand-red/60 hover:bg-brand-red/5 dark:hover:bg-brand-red/10"}`}
            >
                <UploadCloud className="h-6 w-6 text-zinc-400" />
                <div className="font-medium">Drop photos, videos, or PDFs here</div>
                <div className="text-xs text-zinc-500">Up to {MAX_MB} MB each · jpg/png/webp/heic, mp4/mov (≤60s), pdf</div>
                <div className="mt-1 flex flex-wrap gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={disabled}>
                        Browse files
                    </Button>
                    <input
                        ref={inputRef} type="file" multiple accept={ACCEPT}
                        capture="environment" hidden
                        onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
                        data-testid="attachment-upload-input"
                    />
                </div>
            </div>

            {uploadError && (
                <div
                    role="alert"
                    data-testid="upload-error"
                    className="rounded-md border border-brand-red/40 bg-brand-red/10 px-3 py-2 font-mono text-xs text-brand-redDeep dark:text-brand-red"
                >
                    {uploadError}
                </div>
            )}

            {files.length > 0 && (
                <div className="space-y-2 rounded-md border bg-zinc-50 p-3 dark:bg-zinc-900">
                    {files.map((f, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`stage-file-${i}`}>
                            <span className="flex-1 truncate font-mono">{f.file.name}</span>
                            <span className="font-mono text-zinc-500">{(f.file.size / 1024 / 1024).toFixed(2)} MB</span>
                            <select value={f.kind} onChange={(e) => update(i, "kind", e.target.value)}
                                    className="h-8 rounded-md border bg-background px-2">
                                {KIND_OPTIONS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
                            </select>
                            <Input
                                value={f.caption} maxLength={280}
                                onChange={(e) => update(i, "caption", e.target.value)}
                                placeholder="Caption (optional)"
                                className="h-8 max-w-[260px]"
                            />
                            <button type="button" onClick={() => remove(i)} className="text-zinc-400 hover:text-brand-red"><X className="h-4 w-4" /></button>
                        </div>
                    ))}
                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="ghost" size="sm" onClick={() => setFiles([])} disabled={uploading}>Clear</Button>
                        <Button type="button" size="sm" onClick={upload} disabled={uploading}
                                className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="attachment-upload-btn">
                            {uploading && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                            Upload {files.length} file{files.length === 1 ? "" : "s"}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

export function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{label}</Label>
            {children}
        </div>
    );
}
